import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import LanguageSwitchButton from "./buttons/lang-switch-button";
import MenuButton from "./buttons/menu-button";
import UserButton from "./buttons/user-button";
import NavigationMenu, {
  LinkLikeProps,
  NavigationItem,
} from "./navigation-menu";
import MiniCVSection from "../mini-cv";
import RightSwitcher from "./buttons/right-toggle";
import { SkillsChartSlider } from "../../common/skills-chart";
import { SupportedLanguages as Language } from "./buttons/lang-switch-button";
import { NavLink, useLocation } from "react-router-dom";
import { PositionDetails } from "../mini-cv/position-modal";
import { useCvDataExperience } from "../../locales/cv-data";
import { useMiniCvData } from "./mini-cv-data";
import { useSkillsChartSlides } from "../../locales/skills-chart-data";
import { useTranslate } from "../../locales/hooks";
import InfoHint from "./info-hint";
// import { workExperience } from "../../pages/full-cv-page";

interface NavTopMenuWithLangProps {
  language?: Language;
  supportedLanguages?: Language[];
  brandName?: string;
  initialMenuOpen?: boolean;
  navItems?: NavigationItem[];
  LinkComponent?: React.ComponentType<LinkLikeProps>;
  onLanguageChange?: (lang: Language) => void;
  onOpenMenu?: (state: boolean) => void;
}


const NavTopMenuWithLang: React.FC<NavTopMenuWithLangProps> = ({
  language = "RU",
  supportedLanguages = ["RU", "EN"],
  brandName = "VOLOBUEV",
  initialMenuOpen = false,
  navItems = [{ label: "Main", path: "/" }],
  onLanguageChange,
  onOpenMenu,
  LinkComponent,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(initialMenuOpen);
  const [rightSwitcherState, setRightSwitcherState] = useState<
    "mini-cv" | "skills"
  >("mini-cv");

  const location = useLocation();
  const t = useTranslate();
  const workExperience = useCvDataExperience();
  const miniCvData = useMiniCvData();
  const skillsChartSlides = useSkillsChartSlides();
  const prevPathRef = useRef(location.pathname);

  const detailsByPositionId = Object.fromEntries(
  workExperience
    .flatMap((c) => c.positions)
    .map((p) => [
      p.id,
      {
        title: p.title,
        period: p.period,
        duration: p.duration,
        responsibilities: p.responsibilities,
        achievements: p.achievements,
      } satisfies PositionDetails,
    ]),
);


  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setIsMenuOpen(false);
      onOpenMenu?.(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, onOpenMenu]);

  useEffect(() => {
    setIsMenuOpen(initialMenuOpen);
  }, [initialMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        onOpenMenu?.(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen, onOpenMenu]);

  const toggleMenu = () => {
    const nextState = !isMenuOpen;
    setIsMenuOpen(nextState);
    onOpenMenu?.(nextState);
  };

  return (
    <div className="w-full flex justify-center p-4">
      <motion.div
        initial={false}
        animate={
          isMenuOpen
            ? { width: 600, height: "auto" }
            : { width: 320, height: 60 }
        }
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 23,
          duration: 0.4,
        }}
        className="rounded-[10px] border border-[#ddd] bg-white overflow-hidden"
      >
        <div className="flex flex-col gap-[10px] h-full">
          {/* Header */}
          <div className="relative flex h-[60px] items-center justify-between">
            <LanguageSwitchButton
              currentLanguage={language}
              supportedLanguages={supportedLanguages}
              onLanguageChange={(lang) => onLanguageChange?.(lang)}
            />

            <div className="flex items-center">
              <UserButton />
              <MenuButton onToggle={toggleMenu} isOpen={isMenuOpen} />
            </div>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="font-['Hind',sans-serif] font-bold text-[16px] text-black pointer-events-auto">
                <NavLink to="/">{brandName}</NavLink>
              </p>
            </div>
          </div>

          {/* {isMenuOpen && ( */}
          <>
            {/* Body */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="gap-[30px] flex flex-1 flex-col md:flex-row md:max-h-[250px]"
            >
              <div className="flex flex-1 flex-col px-[20px] md:pr-[0px]">
                <NavigationMenu
                  activePath="/"
                  navItems={navItems}
                  LinkComponent={LinkComponent}
                />
              </div>

              <div className="flex flex-1 flex-col justify-center px-[20px] md:pl-[0px] gap-[10px]">
                <div className="flex flex-col h-[210px] justify-center ">
                  {rightSwitcherState === "mini-cv" ? (
                    <MiniCVSection
                      cvData={miniCvData}
                      detailsByPositionId={detailsByPositionId}
                    />
                  ) : (
                    <SkillsChartSlider slides={skillsChartSlides} />
                  )}
                </div>

                <RightSwitcher
                  SwitchState={rightSwitcherState}
                  onSwitching={(state) => setRightSwitcherState(state)}
                />
              </div>
            </motion.div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="gap-[10px] h-[60px] items-center flex"
            >
              <div className="flex flex-1 h-full items-center justify-center pl-[20px] md:pl-[0px]">
                <p className="flex items-center gap-[5px] font-['Hind',sans-serif] text-[12px] text-left text-gray-700">
                  {t("menuStats__projects")}
                  <InfoHint text={t("menuStats__projectsHint")} />
                </p>
              </div>

              <div className="flex-1 h-full items-center justify-center hidden md:flex">
                <p className="flex items-center gap-[5px] font-['Hind',sans-serif] text-[12px] text-center text-gray-700">
                  {t("menuStats__reports")} <span>18 / 9</span>
                  <InfoHint text={t("menuStats__reportsHint")} />
                </p>
              </div>

              <div className="flex flex-1 h-full items-center justify-center pr-[20px] md:pr-[0px]">
                <p className="font-['Hind',sans-serif] text-[12px] text-right text-gray-700">
                  {t("menuStats__experience")}
                </p>
              </div>
            </motion.div>
          </>
          {/* )} */}
        </div>
      </motion.div>
    </div>
  );
};

export default NavTopMenuWithLang;
export type { Language };
